import {
  BankAccountResponse,
  ConfirmOfframpResponse,
  CreateSessionRequest,
  CryptoChannelsResponse,
  CryptoPriceResponse,
  HealthResponse,
  IdentifyResponse,
  QuoteResponse,
  RateResponse,
  BankNetwork,
  KycRequirement,
  KycSubmitResponse,
  NextStep,
  SessionResponse,
  SessionStatus,
  VerifyOtpResponse
} from './types.js';
import { ApiError } from './errors.js';

type Fetcher = typeof fetch;

export type PouchPayClientOptions = {
  baseUrl?: string;
  apiKey?: string;
  fetcher?: Fetcher;
  timeoutMs?: number;
  headers?: Record<string, string>;
};

export type PollOptions = {
  intervalMs?: number;
  timeoutMs?: number;
  untilStatus?: SessionStatus[];
};

export class PouchPayClient {
  private baseUrl: string;
  private apiKey?: string;
  private fetcher: Fetcher;
  private timeoutMs: number;
  private headers: Record<string, string>;

  constructor(options: PouchPayClientOptions = {}) {
    const fetcher = options.fetcher ?? (globalThis.fetch ? globalThis.fetch.bind(globalThis) : undefined);
    if (!fetcher) {
      throw new Error('Fetch API not available. Provide a fetcher in PouchPayClientOptions.');
    }

    this.baseUrl = (options.baseUrl ?? 'https://api.pouchfinance.xyz').replace(/\/$/, '');
    this.apiKey = options.apiKey;
    this.fetcher = fetcher;
    this.timeoutMs = options.timeoutMs ?? 15000;
    this.headers = options.headers ?? {};
  }

  setApiKey(apiKey: string) {
    this.apiKey = apiKey;
  }

  async health(): Promise<HealthResponse> {
    return this.request('GET', '/health');
  }

  async createSession(input: CreateSessionRequest): Promise<SessionResponse> {
    return this.request('POST', '/v2/sessions', input);
  }

  async getSession(id: string): Promise<SessionResponse> {
    return this.request('GET', `/v2/sessions/${id}`);
  }

  async getQuote(id: string): Promise<QuoteResponse> {
    return this.request('GET', `/v2/sessions/${id}/quote`);
  }

  async getRate(params: { amount: number; fromCurrency: string; toCurrency: string; type: 'BUY' | 'SELL'; countryCode: string }): Promise<RateResponse> {
    const query = new URLSearchParams({
      amount: params.amount.toString(),
      fromCurrency: params.fromCurrency,
      toCurrency: params.toCurrency,
      type: params.type,
      countryCode: params.countryCode
    });
    return this.request('GET', `/crypto/rate?${query.toString()}`);
  }

  async getCryptoPrice(symbol: string): Promise<CryptoPriceResponse> {
    const query = new URLSearchParams({ symbol });
    return this.request('GET', `/crypto/price?${query.toString()}`);
  }

  async getBankNetworks(id: string): Promise<BankNetwork[]> {
    const response = await this.request<{ networks: BankNetwork[] }>('GET', `/v2/sessions/${id}/bank-networks`);
    return response.networks || [];
  }

  async pollSession(id: string, options: PollOptions = {}): Promise<SessionResponse> {
    const intervalMs = options.intervalMs ?? 3000;
    const timeoutMs = options.timeoutMs ?? 120000;
    const untilStatus = options.untilStatus ?? [];
    const start = Date.now();

    while (true) {
      const session = await this.getSession(id);

      if (untilStatus.length && untilStatus.includes(session.status)) {
        return session;
      }

      if (!untilStatus.length) {
        return session;
      }

      if (Date.now() - start >= timeoutMs) {
        throw new Error('Polling timed out.');
      }

      await this.sleep(intervalMs);
    }
  }

  async identify(sessionId: string, email: string): Promise<IdentifyResponse> {
    return this.request('POST', `/v2/sessions/${sessionId}/identify`, { email });
  }

  async verifyOtp(sessionId: string, code: string): Promise<VerifyOtpResponse> {
    return this.request('POST', `/v2/sessions/${sessionId}/verify-otp`, { code });
  }

  async resendOtp(sessionId: string): Promise<IdentifyResponse> {
    return this.request('POST', `/v2/sessions/${sessionId}/resend-otp`);
  }

  async getKycRequirements(sessionId: string): Promise<KycRequirement> {
    return this.request('GET', `/v2/sessions/${sessionId}/kyc-requirements`);
  }

  async submitKyc(sessionId: string, documents: Record<string, string>): Promise<KycSubmitResponse> {
    return this.request('POST', `/v2/sessions/${sessionId}/kyc`, { documents });
  }

  async verifyBankAccount(sessionId: string, accountNumber: string, networkId: string): Promise<BankAccountResponse> {
    return this.request('POST', `/v2/sessions/${sessionId}/bank-account`, { accountNumber, networkId });
  }

  async confirmOfframp(sessionId: string): Promise<ConfirmOfframpResponse> {
    return this.request('POST', `/v2/sessions/${sessionId}/confirm-offramp`);
  }

  async getCryptoChannels(): Promise<CryptoChannelsResponse> {
    return this.request('GET', '/crypto/channels');
  }

  /**
   * Generate access token for a user (Shared KYC)
   * Token can be used in payment URLs (?at=token) to skip email/OTP auth
   *
   * @param email - User email address
   * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'NG', 'KE')
   * @returns Access token, expiration date, and user ID
   */
  async generateAccessToken(email: string, countryCode: string): Promise<{
    accessToken: string;
    expiresAt: string;
    userId: string;
  }> {
    return this.request('POST', '/v2/payment-auth/tokens', {
      email: email.toLowerCase().trim(),
      countryCode: countryCode.toUpperCase()
    });
  }

  /**
   * Revoke an access token
   *
   * @param token - Access token to revoke
   */
  async revokeAccessToken(token: string): Promise<{ success: boolean }> {
    return this.request('POST', '/v2/payment-auth/tokens/revoke', { token });
  }

  /**
   * Create or get a static deposit address for offramp
   *
   * @param params - Static address creation parameters
   * @returns Static deposit address details
   */
  async createStaticAddress(params: {
    originNetwork: string;
    originAsset?: string;
    settlementAsset?: string;
    countryCode: string;
    currency: string;
    bankAccount: {
      accountNumber: string;
      accountName?: string;
      networkId: string;
    };
    evmRefundAddress: string;
    userKyc?: Record<string, string>;
  }): Promise<{
    depositAddress: string;
    originNetwork: string;
    smartAccountAddress: string;
    destinationHash: string;
    depositInstructions: {
      address: string;
      network: string;
      currency: string;
      tokenAddress?: string;
      instructions: string;
    };
    settlement: {
      bankAccount: any;
      currency: string;
      accountName?: string;
    };
    reusable: boolean;
    message: string;
  }> {
    return this.request('POST', '/v2/offramp/static/initiate', params);
  }

  /**
   * Get existing static deposit address by destination hash
   *
   * @param destinationHash - Unique identifier for the address
   * @returns Static deposit address details
   */
  async getStaticAddress(destinationHash: string): Promise<{
    depositAddress: string;
    smartAccountAddress: string;
    destinationHash: string;
    bankAccount: any;
    currency: string;
    countryCode: string;
    status: string;
    reusable: boolean;
  }> {
    return this.request('GET', `/v2/offramp/static/${destinationHash}`);
  }

  /**
   * Check balance and trigger offramp if sufficient funds
   *
   * @param params - Address identifier (depositAddress or destinationHash)
   * @returns Sync result with balance and trigger status
   */
  async syncStaticAddress(params: {
    depositAddress?: string;
    destinationHash?: string;
  }): Promise<{
    success: boolean;
    message: string;
    balance: number;
    balanceCheckTimestamp: string;
    triggered: boolean;
    depositAddress: string;
    smartAccountAddress: string;
  }> {
    return this.request('POST', '/v2/offramp/static/sync', params);
  }

  /**
   * List all static deposit addresses for the partner
   *
   * @param params - Query parameters
   * @returns List of static addresses with pagination
   */
  async listStaticAddresses(params?: {
    limit?: number;
    offset?: number;
    status?: 'active' | 'inactive';
  }): Promise<{
    addresses: Array<{
      depositAddress: string;
      originNetwork: string;
      smartAccountAddress: string;
      destinationHash: string;
      createdAt: string;
      lastActivityAt: string | null;
      status: string;
      bankAccount: any;
      currency: string;
      countryCode: string;
      stats: {
        totalDeposits: number;
        totalVolume: number;
        lastDepositAt: string | null;
        currentBalance: number | null;
      };
    }>;
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    const query = params ? new URLSearchParams(
      Object.entries(params)
        .filter(([_, v]) => v !== undefined)
        .map(([k, v]) => [k, String(v)])
    ).toString() : '';
    return this.request('GET', `/v2/offramp/static/addresses${query ? `?${query}` : ''}`);
  }

  /**
   * Get supported chains for static offramps
   *
   * @returns Supported chains and token addresses
   */
  async getSupportedChains(): Promise<Record<string, any>> {
    return this.request('GET', '/offramp-static/supported-chains');
  }

  /**
   * Refund stuck USDC from smart account to refund address
   *
   * @param params - Address identifier (depositAddress or destinationHash)
   * @returns Refund transaction details
   */
  async refundStaticAddress(params: {
    depositAddress?: string;
    destinationHash?: string;
  }): Promise<{
    success: boolean;
    message: string;
    balanceRefunded: number;
    transactionHash: string;
    refundedTo: string;
    depositAddress: string;
    smartAccountAddress: string;
  }> {
    return this.request('POST', '/v2/offramp/static/refund', params);
  }

  private buildHeaders(extra?: Record<string, string>, includeJson = false) {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...this.headers,
      ...extra
    };

    if (includeJson) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.apiKey) {
      headers['x-api-key'] = this.apiKey;
    }

    return headers;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    const hasBody = body !== undefined;

    try {
      const response = await this.fetcher(url, {
        method,
        headers: this.buildHeaders(undefined, hasBody),
        body: hasBody ? JSON.stringify(body) : undefined,
        signal: controller.signal
      });

      const payload = await this.parseJson(response);

      if (!response.ok) {
        const message = payload?.error || response.statusText || 'Request failed';
        throw new ApiError(message, response.status, payload);
      }

      return payload as T;
    } finally {
      clearTimeout(timer);
    }
  }

  private async parseJson(response: Response): Promise<any> {
    const text = await response.text();
    if (!text) return null;

    try {
      return JSON.parse(text);
    } catch {
      return { raw: text };
    }
  }

  private async sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
